// src/context/PhoneNumberContext.jsx
import React, { createContext, useContext, useState } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  Button,
  Box,
  Typography,
  IconButton,
} from "@mui/material";
import { Phone, X, Zap, Shield } from "lucide-react";
import { getHeader, launchEsSignup, toastError } from "../utils/util";
import { API_ENDPOINT } from "../utils/constants";

// Create the Context
const PhoneNumberContext = createContext();

// Custom hook for easy access
export const usePhoneNumber = () => {
  const context = useContext(PhoneNumberContext);
  if (!context) {
    throw new Error("usePhoneNumber must be used within a PhoneNumberProvider");
  }
  return context;
};

const OPTIONS = [
  {
    key: "Option A",
    icon: <Zap size={22} />,
    label: "Option A",
    description:
      "Fast-track channel. Gets your number in the pipeline swiftly.",
    accent: "#2563eb",
    bg: "rgba(37, 99, 235, 0.06)",
    hoverBg: "rgba(37, 99, 235, 0.12)",
  },
  {
    key: "Option B",
    icon: <Shield size={22} />,
    label: "Option B",
    description: "Steady-lane channel. Same destination, different route.",
    accent: "#0891b2",
    bg: "rgba(8, 145, 178, 0.06)",
    hoverBg: "rgba(8, 145, 178, 0.12)",
  },
];

// Provider Component
export const PhoneNumberProvider = ({ children }) => {
  const [showPhoneDialog, setShowPhoneDialog] = useState(false);
  const [fetchingCustomerId, setFetchingCustomerId] = useState(false);
  const [loadingOption, setLoadingOption] = useState(null);

  const openPhoneDialog = () => setShowPhoneDialog(true);
  const closePhoneDialog = () => {
    if (!fetchingCustomerId) setShowPhoneDialog(false);
  };

  const handlePhoneOptionClick = async (option) => {
    setShowPhoneDialog(false);
    setFetchingCustomerId(true);
    setLoadingOption(option);

    try {
      const response = await axios.get(
        `${API_ENDPOINT}/root/get-cusid-for-onboarding`,
        getHeader()
      );

      const customerId = response.data.customerId;

      if (option === "Option A") {
        launchEsSignup(false, customerId);
      } else if (option === "Option B") {
        launchEsSignup(true, customerId);
      }
    } catch (err) {
      toastError(err);
    } finally {
      setFetchingCustomerId(false);
      setLoadingOption(null);
    }
  };

  return (
    <PhoneNumberContext.Provider value={{ openPhoneDialog }}>
      {children}

      <Dialog
        open={showPhoneDialog}
        onClose={closePhoneDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "20px",
            background: "#ffffff",
            boxShadow:
              "0 24px 60px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.06)",
            overflow: "visible",
            position: "relative",
          },
        }}
        BackdropProps={{
          sx: {
            backgroundColor: "rgba(15, 23, 42, 0.45)",
            backdropFilter: "blur(4px)",
          },
        }}
      >
        {/* Close button */}
        <IconButton
          onClick={closePhoneDialog}
          disabled={fetchingCustomerId}
          size="small"
          sx={{
            position: "absolute",
            top: 14,
            right: 14,
            color: "#94a3b8",
            "&:hover": { color: "#475569", background: "rgba(0,0,0,0.05)" },
            zIndex: 10,
          }}
        >
          <X size={18} />
        </IconButton>

        <DialogContent sx={{ px: 3.5, pt: 4, pb: 3.5 }}>
          {/* Header */}
          <Box sx={{ textAlign: "center", mb: 3.5 }}>
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: "16px",
                background: "linear-gradient(135deg, #2563eb 0%, #0891b2 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 16px",
                boxShadow: "0 8px 24px rgba(37, 99, 235, 0.3)",
              }}
            >
              <Phone size={26} color="#ffffff" />
            </Box>

            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: "#0f172a",
                fontSize: "1.2rem",
                letterSpacing: "-0.3px",
                mb: 0.75,
                fontFamily: "'DM Sans', sans-serif",
              }}
            >
              Add Phone Numbers
            </Typography>

            <Typography
              variant="body2"
              sx={{ color: "#64748b", fontSize: "0.85rem", lineHeight: 1.5 }}
            >
              Pick a channel below — both are equally reliable.
            </Typography>

            {/* Witty hint */}
            <Box
              sx={{
                mt: 1.5,
                px: 2,
                py: 1,
                background: "rgba(241, 245, 249, 0.8)",
                borderRadius: "10px",
                border: "1px solid #e2e8f0",
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: "#64748b",
                  fontSize: "0.75rem",
                  fontStyle: "italic",
                  lineHeight: 1.6,
                }}
              >
                💡 Think of A &amp; B as two lanes on the same highway — same
                destination, zero difference in reliability. Just pick your
                vibe.
              </Typography>
            </Box>
          </Box>

          {/* Option Buttons */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {OPTIONS.map((opt) => (
              <Button
                key={opt.key}
                onClick={() => handlePhoneOptionClick(opt.key)}
                disabled={fetchingCustomerId}
                fullWidth
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  px: 2.5,
                  py: 1.75,
                  borderRadius: "14px",
                  border: `1.5px solid ${
                    loadingOption === opt.key ? opt.accent : "#e2e8f0"
                  }`,
                  background:
                    loadingOption === opt.key
                      ? opt.bg
                      : "rgba(248,250,252,0.6)",
                  textTransform: "none",
                  textAlign: "left",
                  justifyContent: "flex-start",
                  transition: "all 0.18s ease",
                  "&:hover:not(:disabled)": {
                    border: `1.5px solid ${opt.accent}`,
                    background: opt.hoverBg,
                    transform: "translateY(-1px)",
                    boxShadow: `0 6px 20px ${opt.accent}22`,
                  },
                  "&:disabled": { opacity: 0.6, cursor: "not-allowed" },
                }}
              >
                {/* Icon badge */}
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "10px",
                    background: opt.bg,
                    border: `1px solid ${opt.accent}33`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: opt.accent,
                    flexShrink: 0,
                  }}
                >
                  {loadingOption === opt.key ? (
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        border: `2px solid ${opt.accent}44`,
                        borderTop: `2px solid ${opt.accent}`,
                        borderRadius: "50%",
                        animation: "spin 0.7s linear infinite",
                        "@keyframes spin": {
                          from: { transform: "rotate(0deg)" },
                          to: { transform: "rotate(360deg)" },
                        },
                      }}
                    />
                  ) : (
                    opt.icon
                  )}
                </Box>

                {/* Label + description */}
                <Box sx={{ flexGrow: 1 }}>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.9rem",
                      color: "#0f172a",
                      lineHeight: 1.2,
                      fontFamily: "'DM Sans', sans-serif",
                    }}
                  >
                    {loadingOption === opt.key ? "Connecting…" : opt.label}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      color: "#94a3b8",
                      mt: 0.25,
                      lineHeight: 1.4,
                    }}
                  >
                    {opt.description}
                  </Typography>
                </Box>

                {/* Arrow */}
                {loadingOption !== opt.key && (
                  <Box sx={{ color: "#cbd5e1", flexShrink: 0 }}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path
                        d="M6 12l4-4-4-4"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </Box>
                )}
              </Button>
            ))}
          </Box>

          {/* Cancel */}
          <Box sx={{ textAlign: "center", mt: 2.5 }}>
            <Button
              onClick={closePhoneDialog}
              disabled={fetchingCustomerId}
              sx={{
                color: "#94a3b8",
                textTransform: "none",
                fontSize: "0.8rem",
                fontWeight: 500,
                "&:hover": { color: "#475569", background: "transparent" },
              }}
            >
              Maybe later
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </PhoneNumberContext.Provider>
  );
};
